export type SignupGoalId =
  | 'buyer_audit'
  | 'brsr_compliance'
  | 'baseline_footprint'
  | 'green_finance'
  | 'pat_icm'
  | 'cost_reduction';

export const SIGNUP_GOAL_STORAGE_KEY = 'ci-signup-goal-v1';

export interface SignupGoalOption {
  id: SignupGoalId;
  label: string;
  description: string;
  /** Hint shown after company registration */
  nextStepHint: string;
}

export const SIGNUP_GOAL_OPTIONS: SignupGoalOption[] = [
  {
    id: 'buyer_audit',
    label: 'Respond to a buyer or bank audit',
    description: 'Upload evidence and share audit-ready sustainability reports quickly.',
    nextStepHint: 'Gather utility bills and registration IDs — buyers often ask for both.',
  },
  {
    id: 'brsr_compliance',
    label: 'BRSR / regulatory disclosure',
    description: 'Map mandates, facilities, and Scope 3 for SEBI-aligned reporting.',
    nextStepHint: 'Select applicable mandates during company registration to tailor your workflow.',
  },
  {
    id: 'baseline_footprint',
    label: 'Establish a carbon baseline',
    description: 'Run your first assessment with scope 1–3 breakdown.',
    nextStepHint: 'Complete company profile and operations boundaries before the assessment wizard.',
  },
  {
    id: 'green_finance',
    label: 'Unlock green finance',
    description: 'Build verified activity data for loan and incentive applications.',
    nextStepHint: 'Strong document evidence improves lender confidence — plan uploads early.',
  },
  {
    id: 'pat_icm',
    label: 'PAT / Indian Carbon Market readiness',
    description: 'Track energy intensity and compliance tracks for large manufacturers.',
    nextStepHint: 'Enable PAT or ICM mandates in your enterprise profile if they apply.',
  },
  {
    id: 'cost_reduction',
    label: 'Cut energy and operating costs',
    description: 'Prioritize reduction actions with savings and payback estimates.',
    nextStepHint: 'Operations data unlocks ROI-ranked recommendations on your dashboard.',
  },
];

export const isSignupGoalId = (value: string | null | undefined): value is SignupGoalId =>
  SIGNUP_GOAL_OPTIONS.some((o) => o.id === value);

export const getStoredSignupGoal = (): SignupGoalId | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SIGNUP_GOAL_STORAGE_KEY);
    return isSignupGoalId(raw) ? raw : null;
  } catch {
    return null;
  }
};

export const setStoredSignupGoal = (goal: SignupGoalId | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (!goal) {
      localStorage.removeItem(SIGNUP_GOAL_STORAGE_KEY);
    } else {
      localStorage.setItem(SIGNUP_GOAL_STORAGE_KEY, goal);
    }
  } catch {
    /* ignore */
  }
};

export const getSignupGoalOption = (id: SignupGoalId | null | undefined): SignupGoalOption | undefined =>
  SIGNUP_GOAL_OPTIONS.find((o) => o.id === id);
